import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "@/lib/store";
import { autopayStateKey, type RecipientAutopayState } from "@/services/vendor-autopay.service";
import { getRecipientAutopayStates } from "./payment-dues.api";

export function useAutopayStates() {
  const activeOrgId = useAppSelector((s) => s.organization.activeOrgId);
  const [states, setStates] = useState<Map<string, RecipientAutopayState>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!activeOrgId) {
      setLoading(false);
      return;
    }

    // Tag each request so a slow response from a previous org (or an older
    // refresh) cannot clobber the result of the latest one.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextStates = await getRecipientAutopayStates();
      if (requestId !== requestIdRef.current) return;
      setStates(new Map(nextStates.map((state) => [autopayStateKey(state.recipientType, state.recipientId), state])));
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.message ?? "Unable to load autopay settings.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeOrgId]);

  useEffect(() => {
    setStates(new Map());
    void refresh();
  }, [refresh]);

  // Merge one updated entry after an enable/disable response, avoiding a full
  // refetch while keeping the map immutable for React change detection.
  const applyState = useCallback((state: RecipientAutopayState) => {
    setStates((prev) => {
      const next = new Map(prev);
      next.set(autopayStateKey(state.recipientType, state.recipientId), state);
      return next;
    });
  }, []);

  return { states, loading, error, refresh, applyState };
}
